import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSolana } from '../../../hooks/useSolana';
import { useClaimPromoGiftCodeMutation } from '../../../redux/api/authorized/promotional';
import { useGetPromoCodeDetailsQuery } from '../../../redux/api/public/promotional';
import { toast } from 'react-toastify';
import SendingStatus from '../SendingStatus';
import {
  selectLastUpdated,
  selectTokenByNetworkAndSymbol,
} from '../../../redux/slices/tokenSlice';
import GiftSkeleton from './ClaimGiftSkeleton';
import { selectPrivyToken } from '../../../redux/slices/auth';
import {
  EVENT_PROMO_GIFTCARD_CLAIMED,
  sendEvent,
} from '../../../common/analytics';
import PresentationalGift from './PresentationalGift';
import {
  useGetTokenPriceQuery,
  useLazySearchTokensQuery,
} from '../../../redux/api/public/tokens';
import { formatTokenPrice } from '../../../common/currency';
import { openModal } from '../../../redux/slices/modals';
import { useLazyGetGiftQuery } from '../../../redux/api/authorized/gifts';
import { useGetCurrentUserProfileQuery } from '../../../redux/api/authorized/auth';
import useCustomLogin from '../../../hooks/useCustomLogin';

const DEFAULT_IMAGE = '/promo-cards/deks.webp';

const ClaimPromoCodeGift = () => {
  const { id: promoCode } = useParams();
  const [claimError, setClaimError] = useState(null);
  const navigate = useNavigate();
  const { fromTokenAmount, walletsReady } = useSolana();
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [giftData, setGiftData] = useState(null);
  const [claimPromoGiftCode] = useClaimPromoGiftCodeMutation();
  const dispatch = useDispatch();
  const [triggerGetGift] = useLazyGetGiftQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const privyToken = useSelector(selectPrivyToken);
  const { login } = useCustomLogin();

  const { data: userData } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });
  const currentUser = userData;
  const { data, isLoading, isSuccess } = useGetPromoCodeDetailsQuery(
    promoCode,
    {
      skip: !promoCode,
    },
  );

  const [searchTokens] = useLazySearchTokensQuery();
  const lastUpdated = useSelector(selectLastUpdated);

  const token = useSelector((state) =>
    selectTokenByNetworkAndSymbol(
      state,
      'solana',
      data?.token ? data?.token : 'USDC',
    ),
  );

  const { data: tokenPrice } = useGetTokenPriceQuery(token?.address, {
    skip: !token,
    pollingInterval: 10000,
  });

  useEffect(() => {
    if (!token && lastUpdated > 0) {
      searchTokens({
        name: data?.token,
      });
    }
  }, [lastUpdated, data?.token, token]);

  const type = data?.type;

  const isDoubleable = type === 'DOUBLE';

  const cardImage = data?.image ? data?.image : DEFAULT_IMAGE;

  const currentUsdValue =
    tokenPrice?.priceUSDC && data?.amount
      ? parseFloat(data?.amount) * tokenPrice?.priceUSDC
      : 0;

  const handleGiftClaimed = () => {
    if (data?.used === true) {
      // Check if this is the current user's claimed gift
      const isReceiver = giftData?.receiver?.id === currentUser?.id;

      if (!isReceiver) {
        // Redirect to discover page for claimed gifts that aren't theirs
        navigate('/app/discover');
        return;
      }
    }
    if (type === 'PACK') {
      dispatch(
        openModal({
          name: 'diversify',
          props: {
            gift: giftData,
          },
        }),
      );
    } else {
      dispatch(
        openModal({
          name: 'giftClaimed',
          props: {
            gift: giftData,
          },
        }),
      );
    }
  };

  // Handle auto-claim after OAuth login
  useEffect(() => {
    const shouldAutoClaim = searchParams.get('autoClaim');
    if (
      shouldAutoClaim === 'true' &&
      privyToken &&
      sendingStatus === 'idle' &&
      walletsReady
    ) {
      // Clean up the query parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('autoClaim');
      setSearchParams(newSearchParams, { replace: true });

      // Trigger the claim
      handleClaim();
    }
  }, [privyToken, searchParams, sendingStatus, walletsReady]);

  // Auto-trigger gift claimed modal when successfully claimed (skip success screen button)
  useEffect(() => {
    if (sendingStatus === 'success' && giftData) {
      handleGiftClaimed();
    }
  }, [sendingStatus, giftData]);

  useEffect(() => {
    const imagesToLoad = [];

    // Load the gift card image if it exists
    if (data?.image) {
      const img = new Image();
      imagesToLoad.push(
        new Promise((resolve) => {
          img.onload = () => {
            resolve();
          };
          img.onerror = () => {
            resolve();
          };
          img.src = data.image;
        }),
      );
    }

    // Create separate Image objects for each hidden gift image
    const hiddenGiftImg1 = new Image();
    imagesToLoad.push(
      new Promise((resolve) => {
        hiddenGiftImg1.onload = resolve;
        hiddenGiftImg1.onerror = resolve;
        hiddenGiftImg1.src = '/hidden-gift.webp';
      }),
    );

    const hiddenGiftImg2 = new Image();
    imagesToLoad.push(
      new Promise((resolve) => {
        hiddenGiftImg2.onload = resolve;
        hiddenGiftImg2.onerror = resolve;
        hiddenGiftImg2.src = '/christmas-gift-card.webp';
      }),
    );

    const hiddenGiftImg3 = new Image();
    imagesToLoad.push(
      new Promise((resolve) => {
        hiddenGiftImg3.onload = resolve;
        hiddenGiftImg3.onerror = resolve;
        hiddenGiftImg3.src = '/halloween-gift-card.webp';
      }),
    );

    Promise.all(imagesToLoad).then(() => {
      setImagesLoaded(true);
    });
  }, [data?.image, data?.wrappingType]);

  const executeClaim = async () => {
    const result = await claimPromoGiftCode(promoCode).unwrap();

    sendEvent(EVENT_PROMO_GIFTCARD_CLAIMED, {
      id: data.id,
      token: data.token,
      amount: data.amount,
    });

    return result;
  };

  const handleClaim = async () => {
    if (!privyToken) {
      // Add query parameter to track claim intent across OAuth redirect
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('autoClaim', 'true');
      setSearchParams(newSearchParams, { replace: true });

      login();
    } else {
      if (isDoubleable) {
        dispatch(
          openModal({
            name: 'doubleGift',
            props: {
              promoGift: data,
              promoCode: promoCode,
            },
          }),
        );
      } else {
        try {
          setClaimError(null);
          setSendingStatus('sending');

          const result = await executeClaim();
          setGiftData(result.gift);
          setSendingStatus('success');
        } catch (err) {
          const errorMessage = err.data?.message || 'Failed to claim gift';
          setClaimError(errorMessage);
          setSendingStatus('error');
          toast.error(errorMessage);
        }
      }
    }
  };

  if (!promoCode) {
    return <p>No promo code provided</p>;
  }

  if (isLoading || !data?.amount || !tokenPrice?.priceUSDC || !imagesLoaded) {
    return <GiftSkeleton />;
  }
  if (sendingStatus === 'idle') {
    // Determine if current user is the receiver of the claimed gift
    const isReceiver =
      data?.used === true && giftData?.receiver?.id === currentUser?.id;

    return (
      <PresentationalGift
        usdAmount={currentUsdValue.toFixed(2)}
        tokenSymbol={data?.token}
        tokenAmount={formatTokenPrice(data?.amount)}
        token={token}
        giftCardImageUrl={cardImage}
        status={data?.used === true && 'CLAIMED'}
        onClick={data?.used === true ? handleGiftClaimed : handleClaim}
        buttonText={data?.used === true ? 'See Portfolio' : 'Get Gift'}
        showAppDownload={data?.showAppDownload}
        animated={true}
        giftId={promoCode}
        wrappingType={data?.wrappingType ? data?.wrappingType : 'default'}
        isCreator={false}
        isReceiver={isReceiver}
      />
    );
  }

  return (
    <SendingStatus
      email={giftData?.receiver?.email}
      token={token}
      cardImage={cardImage}
      isSuccess={sendingStatus === 'success'}
      isError={sendingStatus === 'error'}
      usdAmount={currentUsdValue.toFixed(2)}
      tokenAmount={giftData ? fromTokenAmount(data?.amount) : '1'}
      successText='Your gift has been claimed! '
      errorText={claimError || 'Failed to claim gift'}
      successButtonText='See Portfolio'
      errorButtonText='Try Again'
      successAction={handleGiftClaimed}
      errorAction={() => setSendingStatus('idle')}
      loadingText='Claiming gift...'
    />
  );
};

export default ClaimPromoCodeGift;
